/**
 * ONE generic evidence-peek panel (§17). A single slide-in surface renders every
 * source kind with its own styling — email header, Slack bubble, CRM row, cell
 * ref, page ref, web card, derived breakdown — and cited-value detail with
 * multi-dimensional verification + lineage. It is a peek, NOT a document viewer:
 * it shows the cited evidence in place; it does not embed the source app.
 */

import type { ReactNode } from "react";
import { ArrowLeft, ExternalLink, X } from "lucide-react";

import type {
	CitedValue,
	CommsSourceRef,
	DerivedSourceRef,
	ExcelSourceRef,
	PdfSourceRef,
	RecordSourceRef,
	SourceRef,
	WebSourceRef,
} from "../../types/citationArtifact";
import { useDopplerUriOpener } from "../../hooks/useDopplerUriOpener";
import { openDopplerUri } from "../../utils/dopplerUri";
import {
	commsHref,
	commsMessageAnchor,
	commsThreadName,
	recordAsOf,
} from "../../utils/sourceLabels";
import { describeSource } from "./sourceKind";
import { SourceChip } from "./SourceChip";
import { VerificationBadges } from "./VerificationBadges";
import { LineageView } from "./LineageView";
import type { PeekTarget } from "./EvidencePeekContext";

// --- Per-kind source bodies -------------------------------------------------

function Field({ label, value }: { label: string; value: ReactNode }) {
	return (
		<div className="flex gap-2 py-0.5 text-sm">
			<span className="w-24 flex-shrink-0 text-text-muted">{label}</span>
			<span className="min-w-0 break-words text-text-base">{value}</span>
		</div>
	);
}

function OpenThreadButton({ ref: r, tone }: { ref: CommsSourceRef; tone: string }) {
	// Opens the markdown projection the message was ingested into. Absent when
	// the ref does not name one, so the panel never offers a dead link.
	//
	// DEFERRED: the URI carries ?message=<n>, but there is no in-app markdown
	// viewer to honour it — the document opens at the top, not at its
	// "## Message N" heading. See utils/dopplerUri.ts.
	const href = commsHref(r);
	if (!href) return null;
	return (
		<button
			type="button"
			onClick={() => openDopplerUri(href)}
			className={`mt-2 inline-flex items-center gap-1 text-xs font-medium hover:underline ${tone}`}
		>
			<ExternalLink className="h-3 w-3" aria-hidden /> Open thread in Doppler
		</button>
	);
}

function EmailBody({ ref: r }: { ref: CommsSourceRef }) {
	const anchor = commsMessageAnchor(r);
	return (
		<div>
			<div className="rounded-t-md border border-amber-200 bg-amber-50 p-3">
				<Field label="Subject" value={commsThreadName(r)} />
				<Field label="From" value={r.sender} />
				<Field label="Sent" value={r.sent_at} />
				{anchor && <Field label="Message" value={anchor} />}
			</div>
			<blockquote className="rounded-b-md border border-t-0 border-amber-200 bg-white p-3 text-sm italic text-text-base">
				“{r.excerpt}”
			</blockquote>
			<OpenThreadButton ref={r} tone="text-amber-700" />
		</div>
	);
}

function SlackBody({ ref: r }: { ref: CommsSourceRef }) {
	const anchor = commsMessageAnchor(r);
	return (
		<div>
			<div className="mb-2 font-mono text-xs text-fuchsia-700">
				{commsThreadName(r)}
				{anchor ? ` · ${anchor}` : ""}
			</div>
			<div className="flex gap-2">
				<div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-fuchsia-100 text-sm font-semibold text-fuchsia-700">
					{r.sender.slice(0, 1).toUpperCase()}
				</div>
				<div className="min-w-0">
					<div className="flex items-baseline gap-2">
						<span className="font-semibold text-text-strong">{r.sender}</span>
						<span className="text-xs text-text-muted">{r.sent_at}</span>
					</div>
					<div className="mt-1 rounded-lg rounded-tl-sm bg-fuchsia-50 px-3 py-2 text-sm text-text-base">
						{r.excerpt}
					</div>
					<OpenThreadButton ref={r} tone="text-fuchsia-700" />
				</div>
			</div>
		</div>
	);
}

function RecordBody({ ref: r }: { ref: RecordSourceRef }) {
	// No "open record" affordance: a CRM row is a database field, not a file,
	// and there is no endpoint that renders one. DEFERRED to v2.
	const asOf = recordAsOf(r);
	return (
		<div className="overflow-hidden rounded-md border border-violet-200">
			<div className="bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-700">
				{describeSource(r).system} record
			</div>
			<div className="divide-y divide-gray-100 p-3">
				{r.record_label && <Field label="Record" value={r.record_label} />}
				<Field label="Object" value={r.object_type} />
				<Field label="Id" value={<span className="font-mono text-xs">{r.record_id}</span>} />
				<Field label="Field" value={r.field} />
				{asOf && <Field label="As of" value={asOf} />}
				{r.version && <Field label="Version" value={r.version} />}
				<Field label="Snapshot" value={<span className="font-mono text-xs">{r.snapshot_sha256.slice(0, 16)}…</span>} />
			</div>
		</div>
	);
}

function ExcelBody({ ref: r, chatId }: { ref: ExcelSourceRef; chatId?: string }) {
	const loc = [r.sheet, r.cell ?? r.range].filter(Boolean).join("!");
	const href = `doppler://excel/${r.file_id}?sheet=${encodeURIComponent(r.sheet)}${r.cell ? `&cell=${encodeURIComponent(r.cell)}` : ""}`;
	return (
		<div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
			{r.filename && <Field label="Workbook" value={r.filename} />}
			<Field label="Cell" value={<span className="font-mono">{loc}</span>} />
			{r.label && <Field label="Row" value={r.label} />}
			{(r.display_value ?? r.raw_value) != null && (
				<Field label="Value" value={String(r.display_value ?? r.raw_value)} />
			)}
			{r.formula && <Field label="Formula" value={<span className="font-mono text-xs">{r.formula}</span>} />}
			{r.cell && (
				<button
					type="button"
					onClick={() => openDopplerUri(href, { chatId: chatId ?? undefined })}
					className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"
				>
					<ExternalLink className="h-3 w-3" aria-hidden /> Open cell in Doppler
				</button>
			)}
		</div>
	);
}

function PdfBody({ ref: r, chatId }: { ref: PdfSourceRef; chatId?: string }) {
	// Same code path as the chat's citation anchors and the evidence cards: the
	// in-app viewer, opened at the cited page with the excerpt highlighted.
	const openDopplerHref = useDopplerUriOpener();
	const params = new URLSearchParams();
	if (r.page != null) params.set("page", String(r.page));
	if (r.excerpt) params.set("hl", r.excerpt);
	const query = params.toString();
	const href = `doppler://pdf/${r.file_id}${query ? `?${query}` : ""}`;
	return (
		<div className="rounded-md border border-rose-200 bg-rose-50 p-3">
			{r.filename && <Field label="Document" value={r.filename} />}
			<Field label="Page" value={r.page} />
			{r.excerpt && (
				<blockquote className="mt-2 border-l-2 border-rose-300 pl-2 text-sm italic text-text-base">
					“{r.excerpt}”
				</blockquote>
			)}
			<button
				type="button"
				onClick={() =>
					openDopplerHref(href, {
						chatId: chatId ?? undefined,
						filename: r.filename ?? undefined,
					})
				}
				className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-rose-700 hover:underline"
			>
				<ExternalLink className="h-3 w-3" aria-hidden /> Open page in Doppler
			</button>
		</div>
	);
}

function WebBody({ ref: r }: { ref: WebSourceRef }) {
	return (
		<div className="rounded-md border border-sky-200 bg-sky-50 p-3">
			{r.title && <Field label="Title" value={r.title} />}
			{r.publisher && <Field label="Publisher" value={r.publisher} />}
			<Field label="Retrieved" value={r.retrieved_at} />
			{r.snippet && (
				<blockquote className="mt-2 border-l-2 border-sky-300 pl-2 text-sm italic text-text-base">
					“{r.snippet}”
				</blockquote>
			)}
			<a
				href={r.url}
				target="_blank"
				rel="noopener noreferrer"
				className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline"
			>
				<ExternalLink className="h-3 w-3" aria-hidden /> {r.url}
			</a>
		</div>
	);
}

function DerivedBody({ ref: r }: { ref: DerivedSourceRef }) {
	return (
		<div className="rounded-md border border-slate-200 bg-slate-50 p-3">
			<Field label="Operation" value={r.operation} />
			{(r.expression ?? r.formula) && (
				<Field label="Expression" value={<span className="font-mono text-xs">{r.expression ?? r.formula}</span>} />
			)}
			{(r.inputs?.length ?? 0) > 0 && (
				<div className="mt-2">
					<div className="mb-1 text-xs text-text-muted">Inputs</div>
					<div className="flex flex-wrap gap-1">
						{r.inputs.map((inp, i) => (
							<SourceChip key={i} source={inp} />
						))}
					</div>
				</div>
			)}
		</div>
	);
}

function SourceBody({ ref: r, chatId }: { ref: SourceRef; chatId?: string }) {
	switch (r.kind) {
		case "comms":
			return r.source_system === "slack" ? <SlackBody ref={r} /> : <EmailBody ref={r} />;
		case "record":
			return <RecordBody ref={r} />;
		case "excel":
			return <ExcelBody ref={r} chatId={chatId} />;
		case "pdf":
			return <PdfBody ref={r} chatId={chatId} />;
		case "web":
			return <WebBody ref={r} />;
		case "derived":
			return <DerivedBody ref={r} />;
		default:
			return (
				<div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-text-muted">
					This source type has no dedicated view yet.
				</div>
			);
	}
}

// --- Value body -------------------------------------------------------------

function ValueBody({ value }: { value: CitedValue }) {
	return (
		<div>
			<div className="rounded-md border border-gray-200 bg-white p-3">
				<div className="text-lg font-semibold text-text-strong">{value.display}</div>
				{value.metric_label && (
					<div className="text-sm text-text-muted">{value.metric_label}</div>
				)}
				<div className="mt-2">
					<VerificationBadges value={value} />
				</div>
			</div>
			{(value.sources?.length ?? 0) > 0 && (
				<div className="mt-3">
					<div className="mb-1 text-xs font-medium text-text-muted">Sources</div>
					<div className="flex flex-wrap gap-1">
						{value.sources.map((s, i) => (
							<SourceChip key={i} source={s} />
						))}
					</div>
				</div>
			)}
			<LineageView value={value} />
		</div>
	);
}

// --- Panel shell ------------------------------------------------------------

interface EvidencePeekPanelProps {
	target: PeekTarget | null;
	canBack: boolean;
	chatId?: string;
	onBack: () => void;
	onClose: () => void;
}

export function EvidencePeekPanel({
	target,
	canBack,
	chatId,
	onBack,
	onClose,
}: EvidencePeekPanelProps) {
	if (!target) return null;

	const heading =
		target.type === "source"
			? describeSource(target.ref)
			: { system: target.value.metric_label ?? "Cited value", Icon: describeSource(target.value.sources?.[0]).Icon };
	const Icon = heading.Icon;

	return (
		<div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
			{/* Backdrop */}
			<button
				type="button"
				aria-label="Close evidence panel"
				onClick={onClose}
				className="absolute inset-0 bg-black/20"
			/>
			<aside className="relative flex h-full w-[380px] max-w-[92vw] flex-col border-l border-gray-200 bg-white shadow-xl">
				<header className="flex items-center gap-2 border-b border-gray-200 px-3 py-2.5">
					{canBack && (
						<button
							type="button"
							onClick={onBack}
							aria-label="Back"
							className="rounded p-1 text-text-muted hover:bg-gray-100"
						>
							<ArrowLeft className="h-4 w-4" aria-hidden />
						</button>
					)}
					<Icon className="h-4 w-4 text-text-muted" aria-hidden />
					<span className="flex-1 truncate text-base font-medium text-text-strong">
						{heading.system}
					</span>
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="rounded p-1 text-text-muted hover:bg-gray-100"
					>
						<X className="h-4 w-4" aria-hidden />
					</button>
				</header>
				<div className="flex-1 overflow-y-auto p-3">
					{target.type === "source" ? (
						<SourceBody ref={target.ref} chatId={chatId} />
					) : (
						<ValueBody value={target.value} />
					)}
				</div>
			</aside>
		</div>
	);
}

export default EvidencePeekPanel;
